import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { SecondaryLogoComponent } from '../../components/shared/secondary-logo/secondary-logo.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomValidators } from '../sign-up-page/sign-up-form/custom-validator/custom-validators';
import { ResetPassNewService } from '../../services/reset-pass/reset-pass-new.service';
import { PasswordValidator } from './custom-validator/pass-validator';
import { Router } from '@angular/router';
import { VerifyTokenService } from '../../services/reset-pass/verify-token.service';
import { MatDialog } from '@angular/material/dialog';
import { ListenTokenService } from '../../services/reset-pass/listen-token.service';
import { take } from 'rxjs';
import { AppRoutes } from '../../utils/enums/routes.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StatusModalComponent } from '../../components/shared/status-modal/status-modal.component';
import { RESET_PASS_OPTIONS } from '../../utils/constants/reset-pass.constants';
import { InputType } from '../../utils/enums/input-type.enum';
import { IconName } from '../../utils/enums/icon-enum';
import { IconComponent } from '../../components/shared/icon/icon.component';

@Component({
  selector: 'app-reset-pass-new',
  standalone: true,
  imports: [SecondaryLogoComponent, TranslateModule, ReactiveFormsModule, IconComponent],
  templateUrl: './reset-pass-new.component.html',
  styleUrl: './reset-pass-new.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPassNewComponent implements OnInit {
  private readonly passService = inject(ResetPassNewService);
  private readonly verifyTokenService = inject(VerifyTokenService);
  private readonly dialog = inject(MatDialog);
  private readonly listenTokenService = inject(ListenTokenService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public changePass!: FormGroup;
  public isLoading = signal<boolean>(false);
  public errorMessage = signal<string | null>(null);
  public inputType = InputType;
  public iconName = IconName;
  public isPasswordVisible = signal<boolean[]>([false, false]);

  public ngOnInit(): void {
    this.checkToken();
    this.createForm();

    this.changePass.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.errorMessage.set(null);
    });
  }

  private createForm(): void {
    this.changePass = new FormGroup(
      {
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          PasswordValidator(),
        ]),
        repeatPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          PasswordValidator(),
        ]),
      },
      { validators: CustomValidators.passwordMatch() },
    );
  }

  private checkToken(): void {
    const tokenParam = this.listenTokenService.token;

    if (tokenParam) {
      this.verifyTokenService
        .verifyToken({ token: tokenParam })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (error) => {
            if (error.status === 500) {
              const dialogRef = this.dialog.open(StatusModalComponent, {
                ...RESET_PASS_OPTIONS,
                data: {
                  isSuccess: false,
                  title: 'resetPassNew.systemError',
                  description: 'resetPassNew.systemErrorText',
                },
              });
              dialogRef
                .afterClosed()
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                  this.router.navigate([AppRoutes.LOGIN]);
                });
            } else {
              this.router.navigate([AppRoutes.INACTIVE_LINK]);
            }
          },
        });
    }
  }

  public togglePasswordVisibility(index: number): void {
    this.isPasswordVisible.update((current) =>
      current.map((visible, i) => (i === index ? !visible : visible)),
    );
  }

  public setNewPassword(): void {
    if (this.changePass.invalid || this.errorMessage()) {
      this.changePass.markAllAsTouched();

      return;
    }

    const tokenParam = this.listenTokenService.token;

    if (tokenParam) {
      this.isLoading.set(true);

      this.passService
        .sendPass({
          token: tokenParam,
          newPassword: this.changePass.get('password')?.value,
          confirmPassword: this.changePass.get('repeatPassword')?.value,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.router.navigate([AppRoutes.CONGRATULATIONS]);
            this.isLoading.set(false);
          },
          error: (error) => {
            this.isLoading.set(false);

            if (error.error.message === this.translate.instant('resetPassNew.errorSamePass')) {
              const matchesPasswordError = this.translate.instant(
                'resetPassNew.matchesPasswordError',
              );
              this.errorMessage.set(matchesPasswordError);
            } else {
              this.dialog.open(StatusModalComponent, {
                ...RESET_PASS_OPTIONS,
                data: {
                  isSuccess: false,
                  title: 'resetPassNew.systemError',
                  description: 'resetPassNew.systemErrorText',
                },
              });
            }
          },
        });
    } else {
      this.dialog.open(StatusModalComponent, {
        ...RESET_PASS_OPTIONS,
        data: {
          isSuccess: false,
          title: 'resetPassNew.systemError',
          description: 'resetPassNew.systemErrorText',
        },
      });
    }
  }
}
